import React from 'react';
import { useBlockEvents } from '../hooks/useBlockEvents.jsx';
import { useBlockPositioning } from '../hooks/useBlockPositioning.jsx';
import { useResponsiveSettings } from '../hooks/useResponsiveSettings.jsx';
import { useBlockSettings, useBlockAnimations, BlockWrapper } from '../hooks/useBlockSettings.jsx';
import { useAnimation } from '../hooks/useAnimation.jsx';

/**
 * Higher-order component that provides common block functionality
 * This reduces code duplication across block components
 */
export const withBlockEnhancements = (WrappedComponent, defaultEventLabel = '') => {
    const EnhancedComponent = (props) => {
        // Extract block settings
        const { blockSettings, hasSettings } = useBlockSettings(props);
        
        if (!hasSettings) {
            console.warn(`${WrappedComponent.displayName || 'Component'}: No block settings found`);
            return null;
        }
        
        // Initialize hooks
        const events = useBlockEvents(blockSettings, defaultEventLabel);
        const positioning = useBlockPositioning(blockSettings);
        const animations = useBlockAnimations(blockSettings);
        
        // Use animation hook if available (assuming useAnimation is imported from elsewhere)
        const animation = typeof useAnimation !== 'undefined' ? useAnimation(animations.animationProps) : null;
        
        // Create enhanced props to pass to wrapped component
        const enhancedProps = {
            ...props,
            blockSettings,
            events,
            positioning,
            animations,
            animation
        };
        
        return (
            <BlockWrapper
                blockSettings={blockSettings}
                animation={animation}
                positionProps={positioning.positionProps}
                eventHandlers={events}
            >
                <WrappedComponent {...enhancedProps} />
            </BlockWrapper>
        );
    };
    
    EnhancedComponent.displayName = `withBlockEnhancements(${WrappedComponent.displayName || WrappedComponent.name})`;
    
    return EnhancedComponent;
};

/**
 * Hook for common input handling patterns
 */
export const useInputEvents = (blockSettings, setValue) => {
    const events = useBlockEvents(blockSettings, blockSettings.input_label || blockSettings.input_name || '');
    const debounceDelay = parseInt(blockSettings.debounce_delay || 500, 10);
    const debounceTimerRef = React.useRef(null);
    
    const handleInputEvent = (eventType, value) => {
        // Clear existing debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        // Set immediate value for change events
        if (eventType === 'change' && setValue) {
            setValue(value);
        }
        
        // Debounce the event dispatch for 'finished' events
        if (eventType === 'finished') {
            debounceTimerRef.current = setTimeout(() => {
                events.handleChange(value);
            }, debounceDelay);
        } else {
            // Dispatch other events immediately
            const eventMap = {
                change: events.handleChange,
                blur: events.handleBlur,
                focus: events.handleFocus,
                keypress: (val) => events.dispatchAnalyticsEvent('input_keypress', { value: val })
            };
            
            const handler = eventMap[eventType];
            if (handler) handler(value);
        }
    };
    
    return {
        ...events,
        handleInputEvent,
        inputEventProps: {
            onChange: (e) => handleInputEvent('change', e.target.value),
            onBlur: (e) => handleInputEvent('blur', e.target.value),
            onFocus: (e) => handleInputEvent('focus', e.target.value),
            onKeyPress: (e) => handleInputEvent('keypress', e.target.value)
        }
    };
};
